// frontend/src/components/WebAuthnSetup.js
// Modal proposé après login pour activer la biométrie
import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Text, VStack, HStack, Icon, Badge, Box, Divider,
  Alert, AlertIcon, Spinner, useToast,
} from '@chakra-ui/react';
import { FiSmartphone, FiShield, FiTrash2 } from 'react-icons/fi';
import { startRegistration } from '@simplewebauthn/browser';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('adminToken');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...opts,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erreur');
  return data;
}

export default function WebAuthnSetup({ isOpen, onClose }) {
  const [step, setStep] = useState('idle'); // idle | registering | done | error | list
  const [credentials, setCredentials] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const toast = useToast();

  const supported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

  useEffect(() => {
    if (isOpen) {
      loadCredentials();
      setStep('idle');
      setErrorMsg('');
    }
  }, [isOpen]);

  async function loadCredentials() {
    try {
      const rows = await apiFetch('/webauthn/credentials');
      setCredentials(rows);
    } catch {}
  }

  async function handleRegister() {
    setStep('registering');
    setErrorMsg('');
    try {
      // 1. Obtenir les options du serveur
      const opts = await apiFetch('/webauthn/register/begin', { method: 'POST', body: JSON.stringify({}) });

      // 2. La lib gère les conversions base64url ↔ ArrayBuffer et l'appel navigateur
      const credential = await startRegistration(opts);

      // 3. Envoyer la réponse complète au serveur pour vérification
      await apiFetch('/webauthn/register/finish', {
        method: 'POST',
        body: JSON.stringify({ credential, deviceName: getDeviceName() }),
      });

      setStep('done');
      await loadCredentials();
      toast({ title: 'Biométrie activée !', status: 'success', duration: 3000 });
    } catch (e) {
      setErrorMsg(e.message || 'Échec de l\'enregistrement');
      setStep('error');
    }
  }

  async function handleDelete(id) {
    try {
      await apiFetch(`/webauthn/credentials/${id}`, { method: 'DELETE' });
      await loadCredentials();
      toast({ title: 'Appareil supprimé', status: 'info', duration: 2000 });
    } catch (e) {
      toast({ title: e.message, status: 'error', duration: 3000 });
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FiShield} color="green.500" />
            <Text>Connexion biométrique</Text>
          </HStack>
        </ModalHeader>
        <ModalBody>
          {!supported && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Votre navigateur ne supporte pas la biométrie WebAuthn.
            </Alert>
          )}

          {supported && (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Activez la biométrie (empreinte digitale, Face ID…) pour vous connecter sans mot de passe.
              </Text>

              {step === 'error' && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {errorMsg}
                </Alert>
              )}

              {step === 'done' && (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  Biométrie enregistrée avec succès !
                </Alert>
              )}

              {step === 'registering' && (
                <HStack justify="center" py={4}>
                  <Spinner color="green.500" />
                  <Text fontSize="sm">Veuillez suivre les instructions de votre appareil…</Text>
                </HStack>
              )}

              {credentials.length > 0 && (
                <Box>
                  <Divider mb={3} />
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase">
                    Appareils enregistrés
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {credentials.map(c => (
                      <HStack key={c.id} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                        <HStack>
                          <Icon as={FiSmartphone} color="green.500" />
                          <Box>
                            <Text fontSize="sm" fontWeight="600">{c.device_name}</Text>
                            <Text fontSize="xs" color="gray.400">
                              {c.last_used
                                ? `Utilisé le ${new Date(c.last_used).toLocaleDateString('fr-FR')}`
                                : `Ajouté le ${new Date(c.created_at).toLocaleDateString('fr-FR')}`}
                            </Text>
                          </Box>
                        </HStack>
                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() => handleDelete(c.id)}>
                          <Icon as={FiTrash2} />
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>
            {step === 'done' ? 'Continuer' : 'Fermer'}
          </Button>
          {supported && step !== 'registering' && step !== 'done' && (
            <Button colorScheme="green" onClick={handleRegister} leftIcon={<Icon as={FiSmartphone} />}>
              {credentials.length > 0 ? 'Ajouter un appareil' : 'Activer la biométrie'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Helpers ──────────────────────────────────────────────────
function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Mon appareil';
}
